// CHUNK 16: Public landing page at /sinastria/inicio, SEO lead magnet.
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

const FEATURES = [
  { icon: '💕', title: 'Compatibilidade real', desc: 'Score baseado em aspectos entre todos os planetas, não apenas o Sol.' },
  { icon: '⚡', title: 'Forças da relação',    desc: 'Áreas naturais de harmonia e atração magnetica entre vocês.' },
  { icon: '🧭', title: 'Desafios a navegar',  desc: 'Pontos de tensão e como lidar construtivamente com cada um.' },
  { icon: '🎯', title: 'Conselhos práticos',  desc: 'Estratégias personalizadas para fortalecer a conexão.' },
];

export function SynastryLandingPage() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Compatibilidade Astrológica Personalizada | Professya</title>
        <meta name="description" content="Descubra a compatibilidade astrológica entre você e outra pessoa. Análise personalizada com mais de 10 pontos astrológicos. Comece agora." />
        <meta name="keywords" content="sinastria, compatibilidade astrológica, mapa natal, astrologia" />
      </Helmet>

      <div className="min-h-screen bg-[#0D1130]">
        {/* Hero */}
        <section className="py-20 px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="text-[#C9A84C] text-3xl tracking-[8px]" aria-hidden="true">✦</div>
            <h1 className="text-[#C9A84C] text-4xl md:text-5xl font-light tracking-wider leading-tight">
              Descubra Sua Conexão Astrológica
            </h1>
            <p className="text-[#F5ECD7]/70 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Análise completa de compatibilidade baseada em mapas natais personalizados.
              Não é apenas signo solar, são mais de 10 pontos astrológicos analisados.
            </p>
            <button
              onClick={() => navigate('/sinastria/criar')}
              className="inline-block px-10 py-4 bg-[#C9A84C] text-[#0D1130] text-sm uppercase tracking-[2px] font-bold hover:bg-[#C9A84C]/90 transition-colors"
            >
              Começar análise →
            </button>
          </div>
        </section>

        {/* Social proof */}
        <section className="py-12 px-4 border-y border-[#C9A84C]/20">
          <div className="max-w-4xl mx-auto grid grid-cols-3 gap-6 text-center">
            {[
              { value: '50K+',  label: 'análises realizadas' },
              { value: '98%',   label: 'das pessoas recomendam' },
              { value: '4.9 ⭐', label: 'avaliação média' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-[#C9A84C] text-3xl md:text-4xl font-light">{s.value}</div>
                <div className="text-[#F5ECD7]/50 text-xs uppercase tracking-wider mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-[#C9A84C] text-2xl font-light tracking-wider text-center mb-12">
              O que você vai descobrir
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {FEATURES.map(f => (
                <div
                  key={f.title}
                  className="border border-[#C9A84C]/20 rounded-sm p-6 hover:border-[#C9A84C]/40 transition-colors"
                >
                  <div className="text-3xl mb-3" aria-hidden="true">{f.icon}</div>
                  <h3 className="text-[#F5ECD7] font-medium mb-2">{f.title}</h3>
                  <p className="text-[#F5ECD7]/60 text-sm leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Two methods */}
        <section className="py-16 px-4 bg-[#060812]">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-[#C9A84C] text-2xl font-light tracking-wider text-center mb-10">
              Duas formas de criar
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  emoji: '✨',
                  title: 'Convidar alguém',
                  desc:  'Gere um link mágico. A pessoa preenche seus dados e ambos veem o resultado juntos.',
                  badge: 'Recomendado',
                },
                {
                  emoji: '🔒',
                  title: 'Entrada manual',
                  desc:  'Preencha os dados você mesmo. A análise é privada e apenas você vê.',
                  badge: 'Privado',
                },
              ].map(opt => (
                <div key={opt.title} className="border border-[#C9A84C]/20 rounded-sm p-6">
                  <div className="text-2xl mb-3">{opt.emoji}</div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[#F5ECD7] font-medium">{opt.title}</h3>
                    <span className="text-[#C9A84C] text-xs border border-[#C9A84C]/30 px-2 py-0.5">{opt.badge}</span>
                  </div>
                  <p className="text-[#F5ECD7]/60 text-sm leading-relaxed">{opt.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-4 text-center">
          <div className="max-w-xl mx-auto">
            <div className="text-[#C9A84C] text-2xl tracking-[8px] mb-6" aria-hidden="true">✦</div>
            <h2 className="text-[#F5ECD7] text-2xl font-light mb-4">
              Pronta para descobrir?
            </h2>
            <p className="text-[#F5ECD7]/60 text-sm mb-8">
              Análise gratuita com resultado completo. Sem cadastro necessário via convite.
            </p>
            <button
              onClick={() => navigate('/sinastria/criar')}
              className="px-10 py-4 bg-[#C9A84C] text-[#0D1130] text-sm uppercase tracking-[2px] font-bold hover:bg-[#C9A84C]/90 transition-colors"
            >
              ✦ Criar análise agora ✦
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
